import { enqueue, peekAll, remove, count } from './queue.js';
import { api } from './api.js';

const PING_INTERVAL_MS = 10_000;
const FLUSH_INTERVAL_MS = 30_000;
const HEARTBEAT_INTERVAL_MS = 60_000;

function uuid() {
  if (crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export class Tracker {
  constructor(token, callbacks = {}) {
    this.token = token;
    this.cb = callbacks;
    this.watchId = null;
    this.wakeLock = null;
    this.lastEnqueueAt = 0;
    this.lastFix = null;
    this.flushTimer = null;
    this.heartbeatTimer = null;
    this.hiddenSince = 0;
    this.flushing = false;
    this._onVis = this._onVis.bind(this);
    this._onOnline = this._onOnline.bind(this);
    this._onWakeRelease = this._onWakeRelease.bind(this);
  }

  async start() {
    document.addEventListener('visibilitychange', this._onVis);
    window.addEventListener('online', this._onOnline);

    await this._acquireWakeLock();
    this._beginWatch();
    this.flushTimer = setInterval(() => this.flush().catch(() => {}), FLUSH_INTERVAL_MS);
    this.heartbeatTimer = setInterval(() => this._heartbeat(), HEARTBEAT_INTERVAL_MS);

    this._notifyQueue();
  }

  async stop() {
    document.removeEventListener('visibilitychange', this._onVis);
    window.removeEventListener('online', this._onOnline);
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
    if (this.flushTimer) { clearInterval(this.flushTimer); this.flushTimer = null; }
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
    await this._releaseWakeLock();
  }

  async finalFlush() {
    await this.flush(true);
  }

  getLastFix() { return this.lastFix; }

  async flush(force = false) {
    if (this.flushing && !force) return;
    this.flushing = true;
    try {
      const rows = await peekAll(500);
      if (!rows.length) return;
      const pings = rows.map(({ client_id, lat, lng, accuracy, heading, speed, captured_at }) =>
        ({ client_id, lat, lng, accuracy, heading, speed, captured_at }));
      await api.sendPings(this.token, pings);
      await remove(rows.map(r => r.client_id));
      this._notifyQueue();
    } catch (e) {
      this.cb.onSyncError?.(e);
    } finally {
      this.flushing = false;
    }
  }

  _beginWatch() {
    const onPos = (pos) => this._onPosition(pos);
    const onErr = (err) => this._onPositionError(err);
    try {
      this.watchId = navigator.geolocation.watchPosition(onPos, onErr, {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 20000,
      });
    } catch (e) {
      this.cb.onPermissionLost?.(e);
    }
  }

  async _onPosition(pos) {
    const now = Date.now();
    this.lastFix = pos;
    this.cb.onFix?.(pos);
    if (now - this.lastEnqueueAt < PING_INTERVAL_MS) return;
    this.lastEnqueueAt = now;
    await enqueue({
      client_id: uuid(),
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy ?? null,
      heading: Number.isFinite(pos.coords.heading) ? pos.coords.heading : null,
      speed: Number.isFinite(pos.coords.speed) ? pos.coords.speed : null,
      captured_at: pos.timestamp || now,
    });
    this._notifyQueue();
  }

  _onPositionError(err) {
    if (err.code === 1 /* PERMISSION_DENIED */) {
      this.cb.onPermissionLost?.(err);
    } else {
      this.cb.onPositionError?.(err);
    }
  }

  async _heartbeat() {
    try { await api.heartbeat(this.token); } catch {}
  }

  async _acquireWakeLock() {
    if (!('wakeLock' in navigator)) return;
    try {
      this.wakeLock = await navigator.wakeLock.request('screen');
      this.wakeLock.addEventListener('release', this._onWakeRelease);
    } catch (e) {
      this.cb.onWakeLockError?.(e);
    }
  }

  async _releaseWakeLock() {
    if (this.wakeLock) {
      try { await this.wakeLock.release(); } catch {}
      this.wakeLock = null;
    }
  }

  _onWakeRelease() {
    if (document.visibilityState === 'visible') {
      this._acquireWakeLock();
    }
  }

  async _onVis() {
    if (document.visibilityState === 'hidden') {
      this.hiddenSince = Date.now();
      try { navigator.vibrate?.([400, 200, 400, 200, 400]); } catch {}
      this._postPausedNotification();
    } else {
      const hiddenMs = this.hiddenSince ? Date.now() - this.hiddenSince : 0;
      this.hiddenSince = 0;
      this._clearPausedNotification();
      await this._acquireWakeLock();
      if (hiddenMs > 5000) {
        this.cb.onResumeAfterHidden?.(hiddenMs);
      }
      this.flush().catch(() => {});
      this._captureOneNow();
    }
  }

  _onOnline() {
    this.flush().catch(() => {});
  }

  _captureOneNow() {
    navigator.geolocation.getCurrentPosition(
      (p) => this._onPosition(p),
      () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }

  async _postPausedNotification() {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    try {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification('Trip tracking PAUSED', {
        body: 'Tap to resume tracking',
        tag: 'trip-paused',
        renotify: true,
        requireInteraction: true,
        silent: false,
        data: { url: location.pathname },
      });
    } catch {}
  }

  async _clearPausedNotification() {
    if (!('serviceWorker' in navigator)) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const ns = await reg.getNotifications({ tag: 'trip-paused' });
      ns.forEach(n => n.close());
    } catch {}
  }

  async _notifyQueue() {
    try {
      const n = await count();
      this.cb.onQueueChange?.(n);
    } catch {}
  }
}

export function generatePingId() { return uuid(); }
